import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useGamePhase } from '@/hooks/useGamePhase'
import {
  ActivityState
} from '@/redux/slices/timerConfigurationSlice'
import { fetchTimerConfigurationThunk } from '@/redux/asyncThunks/timerConfiguration'
import {
  TimerMode,
  formatPhaseTime,
} from '@/components/settings/timer-configuration/types'
import { cn } from '@/lib/utils'
import { useParams } from 'react-router-dom'
import { NAVY, CARD_BG } from '@/constants/Timeline'
import { getPhaseTypesForRound, getStatusHeadline, getRoundConfigAtIndex, durationFromRoundConfig, durationFromPhaseStatus } from '@/utils/timelineUtils'

const StoryTimeline: React.FC = () => {
  const dispatch = useAppDispatch()
  const params = useParams()
  const roomId = params.roomId as string | undefined
  const { config } = useAppSelector((state) => state.timerConfiguration)
  const timerWebsocketConnected = useAppSelector(
    (state) => state.timerWS.isConnected
  )
  const {
    currentRound,
    totalRounds,
    gameMode,
    manualMode,
    phasesData,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase,
    roundStatus,
  } = useGamePhase()

  const activeRound = currentRound > 0 ? currentRound : 1
  const [open, setOpen] = useState<boolean>(false)
  const [selectedRound, setSelectedRound] = useState<number>(1)
  const [hoveredRound, setHoveredRound] = useState<number | null>(null)
  const prevCurrentRoundRef = useRef<number>(currentRound)
  const userPickedRoundRef = useRef<boolean>(false)
  const hasFetchedConfigRef = useRef<boolean>(false)

  useEffect(() => {
    hasFetchedConfigRef.current = false
  }, [roomId])

  useEffect(() => {
    if (!roomId) return
    const hasDurationsConfig =
      Boolean(config?.roundConfigurations?.length) &&
      Boolean(config?.mode)
    if (hasDurationsConfig) return
    if (!timerWebsocketConnected) return
    if (hasFetchedConfigRef.current) return

    hasFetchedConfigRef.current = true
    dispatch(fetchTimerConfigurationThunk(roomId))
      .unwrap()
      .catch(() => {
        hasFetchedConfigRef.current = false
      })
  }, [
    roomId,
    config?.roundConfigurations?.length,
    config?.mode,
    timerWebsocketConnected,
    dispatch,
  ])

  useEffect(() => {
    const prev = prevCurrentRoundRef.current
    prevCurrentRoundRef.current = currentRound
    if (prev === 0 && currentRound > 0 && !userPickedRoundRef.current) {
      setSelectedRound(currentRound)
    }
  }, [currentRound])

  const handleSelectRound = useCallback((round: number) => {
    userPickedRoundRef.current = true
    setSelectedRound(round)
  }, [])

  const roundNumbers = useMemo(
    () => Array.from({ length: Math.max(1, totalRounds) }, (_, i) => i + 1),
    [totalRounds]
  )

  const hasSavedConfig = Boolean(
    config?.roundConfigurations && config.roundConfigurations.length > 0
  )

  const fallbackRoundConfig = config?.roundConfigurations?.[0] ?? null

  const phasesForSelectedRound = useMemo(() => {
    const types = getPhaseTypesForRound(gameMode as TimerMode, selectedRound)
    const rc = getRoundConfigAtIndex(
      config?.roundConfigurations,
      selectedRound,
      fallbackRoundConfig
    )

    const useManualLive =
      manualMode && selectedRound === activeRound && phasesData?.length

    return types.map((phaseType, phaseIndex) => {
      let minutes: number
      let isInfinite: boolean

      if (useManualLive && phasesData) {
        const live = durationFromPhaseStatus(phasesData, phaseType)
        if (live) {
          minutes = live.minutes
          isInfinite = live.isInfinite
        } else if (rc) {
          const d = durationFromRoundConfig(phaseType, rc)
          minutes = d.minutes
          isInfinite = d.isInfinite
        } else {
          minutes = 0
          isInfinite = false
        }
      } else if (rc) {
        const d = durationFromRoundConfig(phaseType, rc)
        minutes = d.minutes
        isInfinite = d.isInfinite
      } else {
        minutes = 0
        isInfinite = false
      }

      const isCurrentPhase =
        selectedRound === activeRound &&
        ((phaseType === 'randr' && isRandRPhase) ||
          (phaseType === 'advisor' && isAdvisorPhase) ||
          (phaseType === 'negotiation' && isNegotiationPhase) ||
          (phaseType === 'decision' && isDecisionPhase))

      const durationLabel =
        manualMode || (!hasSavedConfig && !(useManualLive && phasesData?.length))
          ? '—'
          : formatPhaseTime(minutes, isInfinite)

      return {
        phaseType,
        label: `Phase ${phaseIndex + 1}`,
        durationLabel,
        isCurrentPhase,
      }
    })
  }, [
    gameMode,
    selectedRound,
    config?.roundConfigurations,
    hasSavedConfig,
    fallbackRoundConfig,
    manualMode,
    activeRound,
    phasesData,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase,
  ])

  const statusHeadline = useMemo(
    () =>
      getStatusHeadline(
        selectedRound,
        activeRound,
        gameMode as TimerMode,
        isRandRPhase,
        isAdvisorPhase,
        isNegotiationPhase,
        isDecisionPhase
      ),
    [
      selectedRound,
      activeRound,
      gameMode,
      isRandRPhase,
      isAdvisorPhase,
      isNegotiationPhase,
      isDecisionPhase,
    ]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          className={cn(
            'h-9 min-h-[36px] w-[9.5rem] bg-[#E6ECF2] text-black border border-black/80',
            'hover:bg-dark-blue hover:text-white shadow-none justify-between rounded-[7px] px-3',
            open &&
              'ring-2 ring-cyan-400/70 shadow-[0_0_14px_rgba(34,211,238,0.45)] border-cyan-600/40'
          )}
          aria-expanded={open}
          aria-label='Open timeline'
        >
          <span className='text-sm font-medium'>Timeline</span>
          <ChevronDown
            className={cn(
              'w-4 h-4 shrink-0 opacity-70 transition-transform',
              open && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        side='bottom'
        sideOffset={8}
        className={cn(
          'flex w-[min(100vw-3rem,460px)] max-h-[min(calc(var(--radix-popover-content-available-height)-2.5rem),560px)] flex-col overflow-hidden p-0 sm:w-[min(100vw-2rem,560px)] sm:max-h-[min(calc(var(--radix-popover-content-available-height)-1.5rem),700px)] border-slate-300/90 shadow-xl',
          'rounded-2xl border'
        )}
        style={{ backgroundColor: CARD_BG }}
      >
        <div className='flex min-h-0 flex-col gap-3.5 p-3.5 sm:gap-5 sm:p-5'>
          <p className='shrink-0 text-sm leading-relaxed text-slate-800'>{statusHeadline}</p>

          <div className='shrink-0 rounded-xl border border-slate-200/90 bg-white/70 p-3 shadow-inner sm:p-4'>
            <div className='relative pt-3 pb-1'>
              <div
                className='absolute left-[8%] right-[8%] top-[calc(50%-6px)] h-1 rounded-full -translate-y-1/2 z-0'
                style={{ backgroundColor: NAVY }}
                aria-hidden
              />
              <div className='relative z-10 flex justify-between items-start gap-1 px-0 sm:px-1'>
                {roundNumbers.map((r) => {
                  const isRoundCompleted =
                    roundStatus === ActivityState.Completed

                  const isCurrent =
                    r === activeRound && !isRoundCompleted
                  const isSelected = r === selectedRound
                  const isPast =
                    r < activeRound || (r === activeRound && isRoundCompleted)
                  const isFuture = r > activeRound
                  const isHover = hoveredRound === r

                  return (
                    <div
                      key={r}
                      className='flex flex-col items-center flex-1 min-w-0 max-w-[20%]'
                    >
                      {isCurrent && (
                        <div
                          className='mb-0.5 w-0 h-0 border-l-[7px] border-r-[7px] border-t-[9px] border-l-transparent border-r-transparent z-20'
                          style={{ borderTopColor: NAVY }}
                          aria-hidden
                        />
                      )}
                      <button
                        type='button'
                        onClick={() => handleSelectRound(r)}
                        onMouseEnter={() => setHoveredRound(r)}
                        onMouseLeave={() => setHoveredRound(null)}
                        className={cn(
                          'flex flex-col items-center gap-1.5 rounded-lg p-1 w-full transition-all',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2',
                          isSelected && !isCurrent && 'ring-2 ring-blue-500/60 ring-offset-1'
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full text-sm font-bold transition-all',
                            isCurrent &&
                              'ring-[3px] ring-cyan-400 bg-white text-[#1a2b4b] shadow-[0_0_12px_rgba(34,211,238,0.55)] scale-105',
                            !isCurrent &&
                              isPast &&
                              'bg-[#2d4a7c] text-white border-2 border-[#1a2b4b]',
                            !isCurrent &&
                              isFuture &&
                              'bg-slate-300 text-slate-600 border-2 border-slate-400',
                            !isCurrent &&
                              !isPast &&
                              !isFuture &&
                              'bg-slate-200 text-slate-700',
                            isHover &&
                              !isCurrent &&
                              'bg-[#2563ab] text-white border-[#1e40af] scale-105'
                          )}
                        >
                          {r}
                        </span>
                        <span
                          className={cn(
                            'text-[10px] sm:text-[11px] text-center font-semibold leading-tight px-0.5',
                            isCurrent ? 'text-[#0e7490]' : 'text-slate-700'
                          )}
                        >
                          Round {r}
                          {isCurrent ? (
                            <span className='block text-cyan-700'>(Current)</span>
                          ) : null}
                        </span>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
            <p className='mt-4 text-center text-xs text-slate-500'>
              A simplified view of your progression through the simulation rounds.
            </p>
          </div>

          <div className='flex min-h-0 flex-1 flex-col rounded-xl border border-slate-200 bg-white/90 p-3 sm:p-3.5'>
            <p className='mb-3 shrink-0 text-sm font-bold text-[#1a2b4b]'>
              Phases — Round {selectedRound}
            </p>
            {!hasSavedConfig && (
              <p className='mb-3 shrink-0 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
                Phase durations appear when the room timer configuration is
                available.
              </p>
            )}
            <ul className='min-h-0 flex-1 space-y-2 overflow-y-auto pr-1'>
              {phasesForSelectedRound.map((p) => (
                <li
                  key={p.phaseType}
                  className={cn(
                    'flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm',
                    p.isCurrentPhase
                      ? 'border-cyan-500/80 bg-cyan-50/90'
                      : 'border-slate-200 bg-white'
                  )}
                >
                  <div className='min-w-0'>
                    <span className='font-medium text-slate-900'>{p.label}</span>
                    {p.isCurrentPhase && (
                      <span className='ml-2 text-xs font-semibold text-cyan-800'>
                        (now)
                      </span>
                    )}
                  </div>
                  <span className='shrink-0 text-slate-600 tabular-nums font-medium'>
                    {p.durationLabel}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default StoryTimeline
